// src/components/admin/student/CustomPlanWorkspacePage.js
//
// Standalone page (its own browser tab / URL) where an admin authors a CUSTOM
// plan for a student — any subject, not limited to SAT/ACT.
//
// Opened with window.open from the student profile Plan tab ("New custom plan"
// / "Edit" on a custom row). Query params:
//   studentId  student directory doc id (required to create)
//   name       student name (display only)
//   planId     existing custom plan to edit (omit to create)
//
// Custom plans are stored in the same `sat_plans` collection with
// `type: "custom"` so they inherit the existing security rules, the student
// Plan tab list/detail views, and tutor session progress tracking. They carry a
// human `title`, an explicit `subjects` order, and a normal checklist whose
// items are admin-typed topics with allocated hours.

import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { auth } from "../../../firebaseConfig";
import {
  fetchPlanById,
  confirmAndActivatePlan,
  saveDraftPlan,
  savePlanEdits,
  setActivePlan,
} from "../../../utils/sat/satPlanData";
import { CUSTOM_PLAN_TYPE } from "../../../utils/plan/planConfig";
import SubjectSelect from "../scheduling/components/SubjectSelect";

const uid = () => `${Date.now().toString(36)}${Math.random().toString(36).slice(2, 8)}`;
const num = (v) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
};
const slug = (s) =>
  String(s || "")
    .toLowerCase()
    .replace(/[^a-z0-9]/g, "");
const round2 = (n) => Math.round(n * 100) / 100;

const blankItem = () => ({ id: uid(), title: "", hours: "2", note: "", isStretch: false, raw: null });
const blankSubject = () => ({ id: uid(), name: "", items: [blankItem()] });

export default function CustomPlanWorkspacePage() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const planId = params.get("planId") || "";
  const studentDirId = params.get("studentId") || "";
  const studentName = params.get("name") || "";

  const [loading, setLoading] = useState(!!planId);
  const [loadError, setLoadError] = useState("");
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);
  const [done, setDone] = useState(false);

  const [title, setTitle] = useState("");
  const [summary, setSummary] = useState("");
  const [reasoning, setReasoning] = useState("");
  const [subjects, setSubjects] = useState([blankSubject()]);

  // Load an existing custom plan for editing (progress fields are preserved).
  useEffect(() => {
    if (!planId) return;
    let alive = true;
    setLoading(true);
    fetchPlanById(planId)
      .then((p) => {
        if (!alive) return;
        if (!p) {
          setLoadError("Plan not found.");
          return;
        }
        if ((p.type || "") !== CUSTOM_PLAN_TYPE) {
          setLoadError("This plan isn't a custom plan. Open it from the Plan tab instead.");
          return;
        }
        setTitle(p.title || "");
        setSummary(p.writtenPlan?.parentSummary || "");
        setReasoning(p.writtenPlan?.reasoning || "");

        const grouped = {};
        const seen = [];
        (p.checklist || []).forEach((it) => {
          if (!grouped[it.subject]) {
            grouped[it.subject] = [];
            seen.push(it.subject);
          }
          grouped[it.subject].push(it);
        });
        const declared = Array.isArray(p.subjects) ? p.subjects.filter((s) => grouped[s]) : [];
        const names = [...declared, ...seen.filter((s) => !declared.includes(s))];
        const toEditorItem = (it) => ({
          id: it.id || uid(),
          title: it.subcategory || "",
          hours: String(it.isStretch ? num(it.rxHours) : num(it.allocatedHours)),
          note: it.guidance || "",
          isStretch: !!it.isStretch,
          raw: it,
        });
        setSubjects(
          names.length
            ? names.map((name) => ({
                id: uid(),
                name,
                items: (grouped[name] || []).map(toEditorItem),
              }))
            : [{ id: uid(), name: "", items: [blankItem()] }]
        );
      })
      .catch((e) => alive && setLoadError(e?.message || "Failed to load the plan."))
      .finally(() => alive && setLoading(false));
    return () => {
      alive = false;
    };
  }, [planId]);

  function patchSubject(si, patch) {
    setSubjects((prev) => prev.map((s, i) => (i === si ? { ...s, ...patch } : s)));
  }
  function addSubject() {
    setSubjects((prev) => [...prev, blankSubject()]);
  }
  function removeSubject(si) {
    setSubjects((prev) => (prev.length <= 1 ? prev : prev.filter((_, i) => i !== si)));
  }
  function patchItem(si, ii, patch) {
    setSubjects((prev) =>
      prev.map((s, i) =>
        i === si ? { ...s, items: s.items.map((it, j) => (j === ii ? { ...it, ...patch } : it)) } : s
      )
    );
  }
  function addItem(si) {
    setSubjects((prev) => prev.map((s, i) => (i === si ? { ...s, items: [...s.items, blankItem()] } : s)));
  }
  function removeItem(si, ii) {
    setSubjects((prev) =>
      prev.map((s, i) => (i === si ? { ...s, items: s.items.filter((_, j) => j !== ii) } : s))
    );
  }

  const totalHours = useMemo(
    () =>
      subjects.reduce(
        (a, s) => a + s.items.filter((i) => !i.isStretch).reduce((x, i) => x + num(i.hours), 0),
        0
      ),
    [subjects]
  );
  const topicCount = useMemo(
    () => subjects.reduce((a, s) => a + s.items.filter((i) => i.title.trim() && !i.isStretch).length, 0),
    [subjects]
  );

  function validate() {
    if (!studentDirId) return "Missing student — reopen this page from the student's Plan tab.";
    if (!title.trim()) return "Give the plan a name.";
    if (!subjects.some((s) => s.name.trim())) return "Add at least one subject.";
    if (!subjects.some((s) => s.items.some((i) => i.title.trim()))) return "Add at least one topic.";
    return "";
  }

  function buildChecklist() {
    const out = [];
    subjects.forEach((subj) => {
      const name = subj.name.trim();
      if (!name) return;
      const valid = subj.items.filter((i) => i.title.trim());
      const required = valid.filter((i) => !i.isStretch);
      const stretch = valid.filter((i) => i.isStretch);
      let subjectRank = 0;
      [...required, ...stretch].forEach((it) => {
        subjectRank += 1;
        const raw = it.raw || {};
        out.push({
          ...raw,
          id: raw.id || it.id,
          subject: name,
          majorCategory: "",
          subcategory: it.title.trim(),
          key: slug(it.title),
          accuracyPct: null,
          total: 0,
          multiplier: 0,
          rxHours: num(it.hours),
          allocatedHours: it.isStretch ? 0 : num(it.hours),
          isStretch: !!it.isStretch,
          priority: out.length + 1,
          subjectRank,
          guidance: it.note || "",
          status: raw.status || "todo",
          progressPct: num(raw.progressPct) || 0,
          coveredHours: num(raw.coveredHours) || 0,
          progressHistory: Array.isArray(raw.progressHistory) ? raw.progressHistory : [],
          completedByUid: raw.completedByUid || null,
          completedByName: raw.completedByName || null,
          completedAt: raw.completedAt || null,
          sessionLogId: raw.sessionLogId || null,
          progressNote: raw.progressNote ?? null,
        });
      });
    });
    return out;
  }

  async function handleSave(activate) {
    const problem = validate();
    if (problem) {
      setError(problem);
      return;
    }
    setSaving(true);
    setError("");
    try {
      const checklist = buildChecklist();
      const fields = {
        type: CUSTOM_PLAN_TYPE,
        title: title.trim(),
        subjects: subjects.map((s) => s.name.trim()).filter(Boolean),
        totalHours: round2(totalHours),
        writtenPlan: { parentSummary: summary || "", reasoning: reasoning || "" },
        checklist,
      };
      const user = auth.currentUser;
      const actor = { uid: user?.uid || null, name: user?.displayName || user?.email || null };

      let savedId = planId;
      if (planId) {
        await savePlanEdits(planId, fields);
        if (activate) await setActivePlan(studentDirId, planId);
      } else {
        const fullPlan = {
          studentDirId,
          studentName,
          diagnosticStudentId: "",
          sourceAttemptId: "",
          testId: "",
          attemptLabel: "",
          sourceCompletedAtMs: null,
          currentScore: null,
          targetScore: null,
          scoreGap: null,
          subjectScores: [],
          snapshot: {
            subjects: [],
            notTested: [],
            summary: {
              requiredCount: checklist.filter((c) => !c.isStretch).length,
              stretchCount: checklist.filter((c) => c.isStretch).length,
              requiredHours: fields.totalHours,
            },
          },
          aiProvider: "custom",
          ...fields,
        };
        savedId = activate
          ? await confirmAndActivatePlan(fullPlan, actor)
          : await saveDraftPlan(fullPlan, actor);
      }

      setSaving(false);
      if (studentDirId) {
        const q = savedId ? `?plan=${encodeURIComponent(savedId)}` : "";
        navigate(`/students/directory/${encodeURIComponent(studentDirId)}/plan${q}`);
      } else {
        setDone(true);
      }
    } catch (e) {
      setError(e?.message || "Failed to save the plan.");
      setSaving(false);
    }
  }

  const close = () => {
    window.close();
    setTimeout(() => {
      if (!window.closed) {
        window.history.length > 1 ? window.history.back() : window.location.replace("/");
      }
    }, 80);
  };

  if (loading) return <div style={st.wrap}>Loading plan…</div>;
  if (loadError) {
    return (
      <div style={st.wrap}>
        <div style={{ color: "#b91c1c", marginBottom: 12 }}>{loadError}</div>
        <button style={st.ghostBtn} onClick={close}>
          Close
        </button>
      </div>
    );
  }
  if (done) {
    return (
      <div style={st.wrap}>
        <div style={{ fontSize: 20, fontWeight: 800, color: "#0f172a", marginBottom: 8 }}>✅ Plan saved</div>
        <p style={{ color: "#64748b", marginBottom: 20 }}>You can close this tab now.</p>
        <button style={st.primaryBtn} onClick={close}>
          Close this tab
        </button>
      </div>
    );
  }

  return (
    <div style={st.page}>
      <header style={st.topbar}>
        <div>
          <div style={st.crumb}>Custom plan{studentName ? ` · ${studentName}` : ""}</div>
          <div style={st.h1}>{planId ? "Edit custom plan" : "New custom plan"}</div>
        </div>
        <button style={st.ghostBtn} onClick={close} disabled={saving}>
          Close
        </button>
      </header>

      <main style={st.main}>
        {error && <div style={st.errBanner}>{error}</div>}

        <section style={st.card}>
          <div style={st.cardTitle}>Plan details</div>
          <label style={st.label}>Plan name</label>
          <input
            style={st.input}
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="e.g. Algebra 2 — Spring tutoring"
          />
          <label style={st.label}>Plan summary (optional)</label>
          <textarea
            style={st.textarea}
            rows={3}
            value={summary}
            onChange={(e) => setSummary(e.target.value)}
            placeholder="A short, parent-friendly summary of what this plan covers."
          />
          <label style={st.label}>Why this plan (optional)</label>
          <textarea
            style={st.textarea}
            rows={2}
            value={reasoning}
            onChange={(e) => setReasoning(e.target.value)}
            placeholder="Optional reasoning shown under the summary."
          />
        </section>

        <section style={st.card}>
          <div style={st.cardHead}>
            <div style={st.cardTitle}>Subjects &amp; topics</div>
            <button style={st.addBtn} onClick={addSubject}>
              + Add subject
            </button>
          </div>
          <div style={st.hint}>
            Pick a subject from the scheduler's subject list, or type your own. Hours are per topic;
            “Not covered” items appear in the plan’s “Not Covered” list with 0 planned hours.
          </div>

          {subjects.map((subj, si) => (
            <div key={subj.id} style={st.subjectCard}>
              <div style={st.subjectHead}>
                <div style={st.subjectSelectWrap}>
                  <SubjectSelect
                    value={subj.name}
                    onChange={(name) => patchSubject(si, { name })}
                    style={{ ...st.input, ...st.subjectName }}
                    placeholder="Subject — pick from the list or type your own"
                  />
                </div>
                {subjects.length > 1 && (
                  <button style={st.iconBtn} onClick={() => removeSubject(si)} title="Remove subject">
                    ✕
                  </button>
                )}
              </div>

              {subj.items.map((it, ii) => (
                <div key={it.id} style={st.itemRow}>
                  <input
                    style={{ ...st.input, flex: "1 1 220px" }}
                    value={it.title}
                    onChange={(e) => patchItem(si, ii, { title: e.target.value })}
                    placeholder="Topic (e.g. Stoichiometry)"
                  />
                  <div style={st.hoursWrap}>
                    <input
                      style={{ ...st.input, width: 64, textAlign: "right" }}
                      inputMode="decimal"
                      value={it.hours}
                      onChange={(e) => patchItem(si, ii, { hours: e.target.value })}
                      placeholder="0"
                    />
                    <span style={st.hoursSuffix}>h</span>
                  </div>
                  <label style={st.checkbox}>
                    <input
                      type="checkbox"
                      checked={it.isStretch}
                      onChange={(e) => patchItem(si, ii, { isStretch: e.target.checked })}
                    />
                    Not covered
                  </label>
                  <button
                    style={st.iconBtn}
                    onClick={() => removeItem(si, ii)}
                    title="Remove topic"
                    disabled={subj.items.length === 1}
                  >
                    ✕
                  </button>
                  <input
                    style={{ ...st.input, flex: "1 1 100%", fontSize: 12.5 }}
                    value={it.note}
                    onChange={(e) => patchItem(si, ii, { note: e.target.value })}
                    placeholder="Notes / guidance for the tutor (optional)"
                  />
                </div>
              ))}

              <button style={st.addBtnSmall} onClick={() => addItem(si)}>
                + Add topic
              </button>
            </div>
          ))}
        </section>
      </main>

      <footer style={st.footer}>
        <div style={st.footerMeta}>
          <strong>{round2(totalHours)}h</strong> planned · {topicCount} {topicCount === 1 ? "topic" : "topics"} ·{" "}
          {subjects.filter((s) => s.name.trim()).length} {subjects.filter((s) => s.name.trim()).length === 1 ? "subject" : "subjects"}
        </div>
        <div style={st.footerActions}>
          <button style={st.ghostBtn} onClick={() => handleSave(false)} disabled={saving}>
            Save as draft
          </button>
          <button style={st.primaryBtn} onClick={() => handleSave(true)} disabled={saving}>
            {saving ? "Saving…" : "Save & activate"}
          </button>
        </div>
      </footer>
    </div>
  );
}

const st = {
  page: { minHeight: "100vh", background: "#f6f7f9", display: "flex", flexDirection: "column" },
  wrap: { padding: 40, fontFamily: "inherit" },

  topbar: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 16,
    background: "#fff",
    borderBottom: "1px solid #eaecef",
    padding: "14px 28px",
  },
  crumb: { fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.06em", color: "#94a3b8" },
  h1: { fontSize: 20, fontWeight: 800, color: "#0f172a", marginTop: 2 },

  main: { flex: 1, width: "100%", maxWidth: 940, margin: "0 auto", padding: "22px 28px 120px" },

  card: { background: "#fff", border: "1px solid #e5e7eb", borderRadius: 14, padding: 20, marginBottom: 16 },
  cardHead: { display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, marginBottom: 4 },
  cardTitle: { fontSize: 13, fontWeight: 800, textTransform: "uppercase", letterSpacing: "0.05em", color: "#6b7280", marginBottom: 10 },
  hint: { fontSize: 12.5, color: "#94a3b8", lineHeight: 1.55, marginBottom: 14 },

  label: { display: "block", fontSize: 12, fontWeight: 700, color: "#64748b", marginBottom: 6, marginTop: 12 },
  input: {
    display: "block",
    width: "100%",
    boxSizing: "border-box",
    border: "1px solid #d1d5db",
    borderRadius: 8,
    padding: "8px 10px",
    fontSize: 13.5,
    color: "#0f172a",
    background: "#fff",
    fontFamily: "inherit",
    minWidth: 0,
  },
  textarea: {
    display: "block",
    width: "100%",
    boxSizing: "border-box",
    border: "1px solid #d1d5db",
    borderRadius: 8,
    padding: "8px 10px",
    fontSize: 13.5,
    color: "#0f172a",
    fontFamily: "inherit",
    lineHeight: 1.55,
    resize: "vertical",
  },

  subjectCard: { border: "1px solid #eef2f7", background: "#fcfdff", borderRadius: 12, padding: 14, marginBottom: 12 },
  subjectHead: { display: "flex", alignItems: "center", gap: 8, marginBottom: 10 },
  subjectSelectWrap: { flex: 1, minWidth: 200 },
  subjectName: { fontWeight: 800, fontSize: 14 },

  itemRow: { display: "flex", flexWrap: "wrap", alignItems: "center", gap: 8, padding: "8px 0", borderTop: "1px solid #f1f5f9" },
  hoursWrap: { display: "flex", alignItems: "center", gap: 4 },
  hoursSuffix: { fontSize: 12.5, color: "#94a3b8", fontWeight: 700 },
  checkbox: { display: "inline-flex", alignItems: "center", gap: 6, fontSize: 12.5, color: "#475569", whiteSpace: "nowrap" },

  addBtn: { border: "1px solid #c7d2fe", background: "#eef2ff", color: "#4338ca", padding: "6px 12px", borderRadius: 8, cursor: "pointer", fontSize: 12.5, fontWeight: 700 },
  addBtnSmall: { marginTop: 8, border: "1px dashed #cbd5e1", background: "transparent", color: "#475569", padding: "6px 12px", borderRadius: 8, cursor: "pointer", fontSize: 12.5, fontWeight: 600 },
  iconBtn: { border: "1px solid #e5e7eb", background: "#fff", color: "#94a3b8", borderRadius: 8, cursor: "pointer", width: 30, height: 30, fontSize: 12, flexShrink: 0 },

  errBanner: { background: "#fef2f2", border: "1px solid #fca5a5", color: "#b91c1c", padding: "10px 12px", borderRadius: 8, fontSize: 13, marginBottom: 14 },

  footer: {
    position: "fixed",
    left: 0,
    right: 0,
    bottom: 0,
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 16,
    flexWrap: "wrap",
    background: "#fff",
    borderTop: "1px solid #e5e7eb",
    padding: "12px 28px",
    boxShadow: "0 -4px 16px rgba(15,23,42,0.05)",
  },
  footerMeta: { fontSize: 13, color: "#475569" },
  footerActions: { display: "flex", gap: 10, alignItems: "center" },

  ghostBtn: { border: "1px solid #d1d5db", background: "#fff", color: "#334155", padding: "8px 14px", borderRadius: 8, cursor: "pointer", fontSize: 13, fontWeight: 600 },
  primaryBtn: { border: "none", background: "#0f172a", color: "#fff", padding: "8px 16px", borderRadius: 8, cursor: "pointer", fontSize: 13, fontWeight: 700 },
};
